import { useRef } from 'react'
import { formatSeconds, useRecorder } from '@/lib/useRecorder'
import { t } from '@/lib/i18n'

/** 一个已经上传的附件。天级作业图恒为 photo；条目级证据两种都有。 */
export type Attachment = {
  id: number
  url: string
  name: string
  kind: 'photo' | 'audio'
  /** 字节数。音频行上显示「84 KB」——时长要解码才知道，不值当。 */
  size?: number
}

/**
 * 稿子 `#attSheet` 的上半截：`.attTiles` 三块砖 + `.attFile` 音频文件兜底 + `#attWhy`。
 *
 * `allowAudio` 为 false 时只有拍照 / 相册两块砖 —— 天级作业图那条路径是
 * 「拍下老师发的那张照片」，录音挂在一天上没有意义（稿子的 renderShots 也只筛照片）。
 *
 * 两个图片 input 而不是一个：`capture="environment"` 在手机上直接开后置摄像头，
 * 不带它才是「从相册选」。同一个 input 上切 capture 属性在 iOS 上不可靠。
 */
export default function AttachmentPicker({ onPick, busy = false, allowAudio = false }: {
  onPick: (files: File[]) => void
  busy?: boolean
  allowAudio?: boolean
}) {
  const shoot = useRef<HTMLInputElement>(null)
  const pick = useRef<HTMLInputElement>(null)
  const audio = useRef<HTMLInputElement>(null)

  const recorder = useRecorder((file) => onPick([ file ]))

  const handle = (el: HTMLInputElement | null) => {
    const files = Array.from(el?.files ?? [])
    // 清空 value：同一个文件连选两次也要能触发 change
    if (el) el.value = ''
    if (files.length) onPick(files)
  }

  const tile =
    'flex flex-col items-center justify-center gap-1 min-h-[76px] flex-1 rounded-ctl ' +
    'border-[1.5px] font-bold text-[13px] active:bg-tint ' +
    'disabled:opacity-40 disabled:pointer-events-none'

  return (
    <>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || recorder.recording}
          onClick={() => shoot.current?.click()}
          className={`${tile} border-line bg-sunk`}
        >
          <span aria-hidden="true" className="text-[22px]">📷</span>
          {t('homework.photo_shoot')}
        </button>
        <button
          type="button"
          disabled={busy || recorder.recording}
          onClick={() => pick.current?.click()}
          className={`${tile} border-line bg-sunk`}
        >
          <span aria-hidden="true" className="text-[22px]">🖼️</span>
          {t('homework.photo_album')}
        </button>

        {/* 稿子在不可用时是**禁用 + 说明原因**，不是隐藏 —— 家长需要知道
            「为什么录不了」和「改用哪个」。一个带解释的禁用控件不是
            「点不动的按钮」，是一个说清了理由的入口。 */}
        {allowAudio && (
          <button
            type="button"
            disabled={busy || !recorder.available}
            onClick={() => (recorder.recording ? recorder.stop() : recorder.start())}
            aria-label={recorder.recording ? t('homework.rec_stop_aria') : t('homework.rec_start_aria')}
            className={
              tile + ' ' +
              (recorder.recording ? 'border-red bg-red-tint text-red-strong' : 'border-line bg-sunk')
            }
          >
            <span aria-hidden="true" className="text-[22px]">{recorder.recording ? '⏹' : '🎙️'}</span>
            {recorder.recording
              ? `${t('homework.rec_stop')} ${formatSeconds(recorder.seconds)}`
              : t('homework.rec_start')}
          </button>
        )}
      </div>

      {/* 不能录 / 权限被拒 / 到上限，都在这里如实说一句。 */}
      {allowAudio && recorder.why && (
        <p className="m-0 mt-2.5 px-3 py-2.5 rounded-ctl bg-amber-tint text-amber-strong text-xs leading-[1.5]">
          {recorder.why}
        </p>
      )}

      {/* 兜底：任何设备都能用。局域网 http 下这是唯一能加音频的路。 */}
      {allowAudio && (
        <button
          type="button"
          disabled={busy || recorder.recording}
          onClick={() => audio.current?.click()}
          className="flex items-center gap-2 w-full min-h-tap mt-2 px-3.5 rounded-ctl bg-sunk text-[13px] font-semibold active:bg-tint disabled:opacity-40"
        >
          <span aria-hidden="true">🎵</span>
          {t('homework.audio_file')}
          <span className="ml-auto text-faint font-normal">{t('homework.audio_file_tail')}</span>
        </button>
      )}

      <input
        ref={shoot} type="file" accept="image/*" capture="environment" multiple hidden
        onChange={(e) => handle(e.currentTarget)}
      />
      <input
        ref={pick} type="file" accept="image/*" multiple hidden
        onChange={(e) => handle(e.currentTarget)}
      />
      <input
        ref={audio} type="file" accept="audio/*" multiple hidden
        onChange={(e) => handle(e.currentTarget)}
      />
    </>
  )
}
